// 051 — Historia Clínica Materna Perinatal (Fase 4, Tanda 1a, ver PLAN.md
// "FASE 4 — spec completa"). Nuevo desde cero — antes corría 100% en Patrón
// A (Questionnaire genérico `051.json`, 16 campos, `extraerHistoriaMaternoPerinatal`
// en extractors.ts).
//
// **Tanda 1a**: bloques de identificación — datos generales, antecedentes
// (familiares/personales/obstétricos), gestación actual y control prenatal
// de la visita actual (`Bloques051Identificacion.tsx` +
// `bloques051IdentificacionDatos.ts`).
// **Tanda 1b (esta, cierra la página 1)**: bloques de Parto/Aborto,
// Postparto, Morbilidad e Intervenciones (`Bloques051Parto.tsx` +
// `bloques051PartoDatos.ts`) — todo `Par[]` de respaldo, sin recurso FHIR
// nuevo (nada acá tiene un consumidor clínico discreto que lo justifique).
// **Tanda 2 (esta, cierra 051)**: Partograma + Recién Nacido/Egresos —
// página 2 (`Bloques051Partograma.tsx` + `bloques051PartogramaDatos.ts`).
// El gráfico dilatación×horas + curva de alerta es un componente visual
// bespoke aparte (`partograma/PartogramaVisual.tsx`, no encaja en
// Questionnaire ni en el motor de bitácora UCI de 113/114/115) — se dibuja
// en el PDF firmado vía `diagramaPartograma` (mismo mecanismo que
// `diagramaDenver`/`diagramaOdontograma`), no como `Par[]` de texto.
//
// FHIR: TODO el formulario, no solo el `Par[]` del PDF. `gestas/partos/abortos/cesareas/fum/fpp` (extensión del Patient,
// `fhir051.ts`); todo el partograma (`partograma/fhir.ts`: un Observation por dato agrupado bajo un panel con `hasMember`); y
// el resto de las secciones —antecedentes, gestación actual, controles prenatales (con altura uterina), postparto, parto/aborto,
// morbilidad, intervenciones, near miss, recién nacido, egresos y anticoncepción— en `perinatal051/` (panel por sección con
// `hasMember`, `Condition` para morbilidad/CIE-10, `Procedure` para intervenciones). Ver `perinatal051/base.ts`.
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DatosPartogramaPdf } from '@logiacare/hce-pdf';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps, Par } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { Bloques051Identificacion } from './Bloques051Identificacion';
import { estadoInicialIdentificacion051, paresDeIdentificacion051 } from './bloques051IdentificacionDatos';
import { Bloques051Parto } from './Bloques051Parto';
import { estadoInicialParto051, paresDeParto051 } from './bloques051PartoDatos';
import { Bloques051Partograma, estadoInicialPagina2Completa051 } from './Bloques051Partograma';
import { paresDePagina2_051 } from './bloques051PartogramaDatos';
import { agregarDatosGestacionales } from './fhir051';
import { agregarDiscretos051 } from './perinatal051';
import { LoteTransaccional } from './transaccionFhir';
import { agregarObservacionesPartograma, numeroDeTexto } from './partograma/fhir';
import { filasHorariasConDato } from './partograma/TablaHoraria';

const CODIGO = '051';

export function Formulario051({ subject, encounter, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { guardando, error, guardar } = useGuardado();

  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('formularios.f051.titulo');
  // Servicio del encabezado del PDF (no el 'Consulta externa' por defecto): CLAP (control prenatal/parto/puerperio): el papel no trae servicio.
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo, { sinServicio: true });

  const [identificacion, setIdentificacion] = useState(() => estadoInicialIdentificacion051());
  const [parto, setParto] = useState(() => estadoInicialParto051());
  const [pagina2, setPagina2] = useState(() => estadoInicialPagina2Completa051());

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const pares: Par[] = [...paresDeIdentificacion051(t, identificacion), ...paresDeParto051(t, parto), ...paresDePagina2_051(t, pagina2)];

      const lote = new LoteTransaccional();
      await agregarDatosGestacionales(lote, medplum, subject, identificacion.antecedentesObstetricos);
      // TODO el 051 en FHIR (antecedentes, gestación, controles con altura uterina, parto, morbilidad, near miss, RN, egresos…): ver perinatal051/base.ts.
      agregarDiscretos051(lote, subject, encounter, { identificacion, parto, pagina2 }, t);

      const puntosPartograma = Object.entries(pagina2.visual.puntos).map(([hora, dilatacion]) => ({ hora: Number(hora), dilatacion }));
      agregarObservacionesPartograma(lote, subject, encounter, pagina2.visual, pagina2.tablaHoraria);
      await lote.enviar(medplum);

      const diagramaPartograma: DatosPartogramaPdf = {
        puntos: puntosPartograma,
        horaBaseLineaAlerta: numeroDeTexto(pagina2.visual.horaBaseLineaAlerta),
        paridad: pagina2.visual.paridad || undefined,
        membranas: pagina2.visual.membranas || undefined,
        filasHorarias: filasHorariasConDato(pagina2.tablaHoraria),
        fcf: pagina2.visual.fcf,
        hodge: pagina2.visual.hodge,
        dips: pagina2.visual.dips,
        rem: pagina2.visual.rem ?? undefined,
        ram: pagina2.visual.ram ?? undefined,
      };

      onGuardado(pares, datosEncabezadoMsp, undefined, undefined, undefined, undefined, diagramaPartograma);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloques051Identificacion estado={identificacion} onChange={setIdentificacion} />

      <Bloques051Parto estado={parto} onChange={setParto} />

      <Bloques051Partograma estado={pagina2} onChange={setPagina2} />

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
